import { defaultGroupAdminCapabilityAdds, defaultGroupOwnerCapabilityAdds, defaultGroupRoles, } from '../types/capabilities.js';
import { checkCapabilities, getGroupCapabilitiesByRoles, } from './capabilitiesHelpers.js';
import { profileName } from './profileHelpers.js';
const withModerator = (member) => ({
    add: [...(member.add ?? []), 'core-posts-delete'],
    ...(member.remove?.length ? { remove: member.remove } : {}),
});
const ownerCapabilities = {
    add: [...defaultGroupOwnerCapabilityAdds],
};
const adminCapabilities = {
    add: [...defaultGroupAdminCapabilityAdds],
};
const containsIgnoreCase = (candidate, query) => candidate.toLowerCase().indexOf(query.toLowerCase()) > -1;
export const groupName = (group) => group?.displayName || group?.handle || '';
/** `#events` selects that tab. Missing or unknown hashes stay on posts. */
export const groupSectionFromHash = (hash) => {
    const name = hash.replace(/^#/, '').split(/[/?&]/)[0];
    if (name === 'events' ||
        name === 'resources' ||
        name === 'description' ||
        name === 'posts') {
        return name;
    }
    return 'posts';
};
/** Shareable group path. Posts is the canonical URL, without a hash. */
export const groupSectionPath = (handle, section = 'posts') => {
    const base = `/groups/@${handle.replace(/^@/, '')}`;
    return section === 'posts' ? base : `${base}#${section}`;
};
export const matchesGroupQuery = (group, query, queryOptions = { handle: true, displayName: true, description: false }) => (queryOptions.handle && containsIgnoreCase(group.handle, query)) ||
    (queryOptions.displayName &&
        group.displayName &&
        containsIgnoreCase(group.displayName, query)) ||
    (queryOptions.description &&
        group.description &&
        containsIgnoreCase(group.description, query));
export const sortGroupMembers = (members) => members?.sort((a, b) => profileName(a.profile).localeCompare(profileName(b.profile))) ?? [];
export const groupMembershipHasRole = (roles, role) => (roles ?? []).includes(role);
export const actorIsGroupOwner = (profile, groupId) => groupMembershipHasRole(profile.memberships?.find((m) => m.group.id === groupId)?.roles, 'owner');
export const countGroupOwners = (members) => members.filter((m) => groupMembershipHasRole(m.roles, 'owner')).length;
export const groupRoleChangeBlocked = ({ actorIsOwner, currentRoles, nextRoles, ownerCount, }) => {
    const wasOwner = groupMembershipHasRole(currentRoles, 'owner');
    const willBeOwner = groupMembershipHasRole(nextRoles, 'owner');
    if (wasOwner !== willBeOwner && !actorIsOwner) {
        return 'owner-assignment';
    }
    if (wasOwner && !willBeOwner && ownerCount <= 1) {
        return 'last-owner';
    }
    return undefined;
};
export const groupOwnerRemovalBlocked = ({ targetRoles, ownerCount, }) => groupMembershipHasRole(targetRoles, 'owner') && ownerCount <= 1;
export const assignableGroupRoles = (actorIsOwner) => actorIsOwner
    ? [...defaultGroupRoles]
    : defaultGroupRoles.filter((role) => role !== 'owner');
export const groupRolesForAssignment = (role) => (role === 'member' ? ['member'] : ['member', role]);
export const canChangeMemberRole = (profile, group) => checkCapabilities(['core-groups-changeMemberRole'], getGroupCapabilitiesByRoles(profile.memberships?.find((m) => m.group.id === group.id)?.roles ?? [], group)).success;
export const canAddMember = (profile, group) => checkCapabilities(['core-groups-addMember'], getGroupCapabilitiesByRoles(profile.memberships?.find((m) => m.group.id === group.id)?.roles ?? [], group)).success;
export const canRemoveMember = (profile, group) => checkCapabilities(['core-groups-removeMember'], getGroupCapabilitiesByRoles(profile.memberships?.find((m) => m.group.id === group.id)?.roles ?? [], group)).success;
export const isGroupDiscoverable = (group) => checkCapabilities(['core-groups-read'], getGroupCapabilitiesByRoles(['none'], group)).success;
/** Posts are only readable by explicit group members (not public/local). */
export const hasMemberOnlyPostsVisibility = (capabilities) => !capabilities?.none?.add?.includes('core-posts-read') &&
    !capabilities?.local?.add?.includes('core-posts-read');
export const groupCapabilityTemplates = {
    defaultGroup: {
        name: 'defaultGroup',
        capabilities: {
            none: {
                add: ['core-groups-read', 'core-posts-read'],
            },
            local: {
                add: [
                    'core-groups-join',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            member: {
                add: [
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            },
            moderator: withModerator({
                add: [
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    defaultGroupClosedCommunity: {
        name: 'defaultGroupClosedCommunity',
        capabilities: {
            local: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    publicGroup: {
        name: 'publicGroup',
        capabilities: {
            none: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-react',
                ],
            },
            member: {
                add: [
                    'core-posts-create-*',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-posts-create-*',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    localGroup: {
        name: 'localGroup',
        capabilities: {
            local: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-react',
                ],
            },
            member: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    privateGroup: {
        name: 'privateGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    limitedPostingGroup: {
        name: 'limitedPostingGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    announcementGroup: {
        name: 'announcementGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    lockedGroup: {
        name: 'lockedGroup',
        capabilities: {
            none: {
                add: ['core-groups-read', 'core-posts-read', 'core-posts-react'],
            },
            member: {
                add: ['core-posts-reply', 'core-posts-rsvp', 'core-posts-vote'],
            },
            moderator: withModerator({
                add: ['core-posts-reply', 'core-posts-rsvp', 'core-posts-vote'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
};
